import * as Sentry from "@sentry/react";
import {
  QueryClient,
  QueryCache,
  MutationCache,
  QueryKey,
} from "@tanstack/react-query";
import { compact, flatten, sortedUniq } from "lodash/fp";

import { ResponseError } from "@app/common/api";
import { isResponseError } from "@app/common/utils";
import logger from "@app/utils/logger";

const QUERY_VAR = "QUERY_VAR";
const DEFAULT = "{{ default }}";

const processQueryKeyPart = (part: unknown) => {
  if (typeof part === "string") {
    // if string is all numbers, replace with QUERY_VAR
    // this is because our queries keys that are all numbers are
    // usually resource ids
    if (/^\d+$/.test(part)) {
      return QUERY_VAR;
    }

    // if the string is all capital letters, return "QUERY_VAR"
    // this is because our queries keys that are all capital letters are
    // usually resource ids
    if (/^[A-Z]+$/.test(part)) {
      return QUERY_VAR;
    }

    // if string is all letters, return as is
    if (/^[a-zA-Z]+$/.test(part)) {
      return part;
    }
  }

  // Objects and arrays are considered to specific to an instance of the query
  // and should be ignored when generating a fingerprint
  return QUERY_VAR;
};

export const getQueryKeyFingerprint = (queryKey: QueryKey) => {
  // remove consecutive duplicates
  // ex: [1, QUERY_VAR, QUERY_VAR, 2, QUERY_VAR, QUERY_VAR, 3] => [1, QUERY_VAR, 2, QUERY_VAR, 3]
  return sortedUniq(queryKey.map(processQueryKeyPart));
};

export const getErrorFingerprint = (error: any) => {
  if (!isResponseError(error)) {
    return [DEFAULT];
  }
  const response = error.response;
  return compact([`${response.status}`, response.statusText]);
};

export const getSentryFingerprint = (queryKey: QueryKey, error: any) => {
  const queryFingerprint = getQueryKeyFingerprint(queryKey);
  const errorFingerprint = getErrorFingerprint(error);
  return flatten([queryFingerprint, errorFingerprint]);
};

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnMount: false } },
  mutationCache: new MutationCache({
    onError: (err, _variables, _context, mutation) => {
      // Catch all non-response based errors as well as all 5xx errors
      if (!isResponseError(err) || err.response.status > 499) {
        // https://docs.sentry.io/platforms/javascript/guides/react/enriching-events/scopes/#local-scopes
        Sentry.withScope((scope) => {
          try {
            scope.setContext("mutation", {
              variables: JSON.parse(JSON.stringify(mutation.state.variables)),
            });
          } catch (e) {
            logger.error("Failed to parse mutation variables", e);
            scope.setContext("mutation", {
              variables: mutation.state.variables,
              variablesError: "failed to parse variables",
            });
          }

          if (isResponseError(err)) {
            const response = (err as ResponseError).response;
            scope.setContext("response", {
              status: response.status,
              statusText: response.statusText,
              url: response.url,
            });
          }
          if (mutation.options.mutationKey) {
            scope.setFingerprint(
              getSentryFingerprint(mutation.options.mutationKey, err)
            );
          }
          Sentry.captureException(err);
        });
      }
    },
  }),
  queryCache: new QueryCache({
    onError: async (err, query) => {
      // Catch all non-response based errors as well as all 5xx errors
      if (!isResponseError(err) || err.response.status > 499) {
        Sentry.withScope((scope) => {
          scope.setContext("query", {
            queryHash: query.queryHash,
          });
          if (isResponseError(err)) {
            const response = (err as ResponseError).response;
            scope.setContext("response", {
              status: response.status,
              statusText: response.statusText,
              url: response.url,
            });
          }
          scope.setFingerprint(getSentryFingerprint(query.queryKey, err));
          Sentry.captureException(err);
        });
      }
    },
  }),
});

export default queryClient;
